import styled, { css } from 'styled-components/macro'

import {
  Accordion,
  Button,
  Divider,
  Layout,
  ProgressBarCountdown,
  Tag,
  Text,
} from '@loadsmart/miranda-react'
import { getToken, toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { getDelivery, getPickup } from 'core/domain/Stop'
import { formatAddress, formatDescription, formatZip } from 'core/presenters/stopPresenter'
import { formatTemperature } from 'core/presenters/temperaturePresenter'
import { ShippingItems } from 'core/shipping-items/ui/ShippingItems'
import { Icon } from 'core/ui/components/icon/Icon'
import { Logo } from 'core/ui/components/logo/Logo'
import { StopsTimeline } from 'core/ui/components/stop/StopsTimeline'
import { formatCurrency } from 'core/utils/currency'

import { screen } from 'legacy/styles/screen'

import { type TenderCarrier, type TenderOffer } from '../domain/TenderOffer'
import {
  formatPickupDate,
  formatDeliveryDate,
  formatTimeRemainingString,
  formatEquipmentType,
  formatWeight,
  getModeIconName,
  getTenderResponseLabel,
} from '../presenters/tenderListCardPresenter'
import { AttachmentList } from './AttachmentList'
import { useTenderExpiration } from './hooks/useTenderExpiration'

// As defined in
// https://www.figma.com/design/Cfeyx9kXJqMhm1fnLlRTjE/-Tokens--Miranda-4all?node-id=2829-11255&t=P2BcrsDSe33gN6vF-4
const MIRANDA_MOBILE_BREAKPOINT = '375px'

export const MoreInfoSectionWrapper = styled(Layout.Group)`
  flex-wrap: wrap;

  ${screen.md} {
    flex-wrap: unset;
  }

  gap: ${getToken('spacing-6')};
`

export const ColumnWrapper = styled(Layout.Stack)`
  flex-basis: 100%;

  ${screen.md} {
    flex-basis: 33.3%;
  }
`

export const StyledLayout = styled.div<{
  $highlighted?: boolean
}>`
  background-color: ${toCSSValue('color-background-primary')};
  border: ${getToken('border-thin')} solid ${toCSSValue('color-border')};
  border-radius: ${getToken('border-radius-s')};
  margin-bottom: ${getToken('spacing-4')};
  overflow: hidden;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      border-color: ${toCSSValue('color-primary-60')};
    `}
`

const HeaderSection = styled(Layout.Group)`
  padding: ${getToken('spacing-2')} ${getToken('spacing-4')};
  border-bottom: ${getToken('border-thin')} solid ${toCSSValue('color-border')};
  background: ${toCSSValue('color-background-secondary')};
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const HorizontalDivider = styled.div<{ fullWidth?: boolean }>`
  height: 2px;
  width: 32px;
  background-color: ${toCSSValue('color-border')};
  flex-shrink: 0;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
        flex-grow: 1;
      }
    `}
`

export const ShowMobile = styled.div`
  display: none;

  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    display: initial;
  }
`

export const HideMobile = styled.div`
  display: initial;

  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const ExpirationContent = styled(Layout.Group)`
  align-items: center;
  gap: ${getToken('spacing-2')};

  [role='progressbar'] {
    flex-grow: 1;
  }
`

const DEFAULT_MAX_PROGRESS_BAR = 100

const StyledText = styled(Text)`
  :after {
    content: ' • ';
  }

  :last-child:after {
    content: none;
  }
`

const NotesList = styled.ul`
  margin: 0;
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: ${getToken('spacing-1')};
`

const StopInfo = styled(Layout.Group)`
  flex-direction: row;

  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    flex-direction: column;
    width: 100%;
  }
`

const ActionsWrapper = styled(Layout.Group)`
  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    margin-top: ${getToken('spacing-4')};
  }
`

const MileageWrapper = styled(Layout.Group)`
  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`

const RateWrapper = styled(Layout.Stack)`
  @media (max-width: ${MIRANDA_MOBILE_BREAKPOINT}) {
    align-items: baseline;
  }
`

export type TenderDetailsProps = Readonly<{
  id: string
  tender: TenderOffer
  carrier?: TenderCarrier
  disabled?: boolean
  onAcceptClick: () => void
  onRejectClick: () => void
  highlighted?: boolean
  responseStatus?: 'accepted' | 'rejected' | null
}>

export function TenderDetails({
  id,
  tender,
  carrier,
  disabled = false,
  onAcceptClick,
  onRejectClick,
  highlighted,
  responseStatus,
}: TenderDetailsProps) {
  const { timeRemaining, progressValue } = useTenderExpiration(tender.expirationDate)
  const { hoursString, minutesString } = formatTimeRemainingString(timeRemaining)

  const pickup = getPickup(tender.stops)
  const delivery = getDelivery(tender.stops)

  if (!pickup || !delivery) {
    return null
  }

  const pickupDateFormatted = formatPickupDate(pickup)
  const deliveryDateFormatted = formatDeliveryDate(delivery)

  const totalWeight = tender.measures?.totalWeight

  return (
    <StyledLayout $highlighted={highlighted} data-testid="tender-list-card">
      {responseStatus && (
        <HeaderSection>
          <Text variant="body-md-bold">{getTenderResponseLabel(tender, responseStatus)}</Text>
        </HeaderSection>
      )}
      <Layout.Stack justify="center">
        <HeaderSection>
          <Layout.Group gap="spacing-1" align="center">
            {tender.shipper.logoUrl && <Logo url={tender.shipper.logoUrl} />}
            <Text variant="body-md" color="color-text-secondary">
              {tender.shipper.companyName}
            </Text>
          </Layout.Group>
          <Layout.Group gap="spacing-0-5">
            <Text variant="body-md">{tender.carrier.email}</Text>
            {carrier?.mc && (
              <>
                <Text variant="body-md" color="color-text-secondary-inverted">
                  &bull;
                </Text>
                <Text variant="body-md">MC {carrier.mc}</Text>
              </>
            )}
            {carrier?.dot && (
              <>
                <Text variant="body-md" color="color-text-secondary-inverted">
                  &bull;
                </Text>
                <Text variant="body-md">DOT {carrier.dot}</Text>
              </>
            )}
          </Layout.Group>
        </HeaderSection>

        <ShowMobile>
          <ExpirationContent paddingX="spacing-4">
            <Text variant="body-md">
              Offer expires in{' '}
              <Text variant="body-md-bold">
                {hoursString}h{minutesString}m
              </Text>
            </Text>
            <ProgressBarCountdown max={DEFAULT_MAX_PROGRESS_BAR} value={progressValue} />
          </ExpirationContent>
        </ShowMobile>

        <Layout.Group
          style={{ display: 'flex' }}
          paddingX="spacing-4"
          gap="spacing-2"
          justify="space-between"
          align="center"
        >
          <StopInfo align="flex-start" justify="flex-start">
            <Layout.Group>
              <Layout.Stack gap="spacing-0-5">
                <Text variant="body-md-bold">
                  {pickup.city}, {pickup.state}
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  {pickupDateFormatted.dateText}
                  {pickupDateFormatted.statusText && (
                    <>
                      {' • '}
                      {pickupDateFormatted.isPendingSchedule ? (
                        <Text color="color-warning-100">{pickupDateFormatted.statusText}</Text>
                      ) : (
                        pickupDateFormatted.statusText
                      )}
                      {pickupDateFormatted.timeText && ` ${pickupDateFormatted.timeText}`}
                    </>
                  )}
                </Text>
              </Layout.Stack>
            </Layout.Group>

            <MileageWrapper
              align="center"
              gap="spacing-2"
              style={{ flexShrink: '0', flexWrap: 'nowrap' }}
            >
              <HorizontalDivider />
              <Text variant="body-sm" color="color-text-secondary" style={{ whiteSpace: 'nowrap' }}>
                {tender.mileage} mi
              </Text>
              <HorizontalDivider fullWidth />
            </MileageWrapper>

            <Layout.Group>
              <Layout.Stack gap="spacing-0-5">
                <Text variant="body-md-bold">
                  {delivery.city}, {delivery.state}
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  {deliveryDateFormatted.dateText}
                  {deliveryDateFormatted.statusText && (
                    <>
                      {' • '}
                      {deliveryDateFormatted.isPendingSchedule ? (
                        <Text color="color-warning-100">{deliveryDateFormatted.statusText}</Text>
                      ) : (
                        deliveryDateFormatted.statusText
                      )}
                      {deliveryDateFormatted.timeText && ` ${deliveryDateFormatted.timeText}`}
                    </>
                  )}
                </Text>
              </Layout.Stack>
            </Layout.Group>
          </StopInfo>
          <ActionsWrapper>
            <RateWrapper align="flex-end" gap="spacing-1">
              <Layout.Group gap="spacing-0-5" align="center">
                <Text variant="body-sm-bold" color="color-primary-100">
                  $
                </Text>
                <Text variant="heading-md-bold">{formatCurrency(tender.flatRate, false, '')}</Text>
              </Layout.Group>

              <Text variant="body-sm">
                per mile <Text variant="body-sm">{formatCurrency(tender.pricePerMile, true)}</Text>
              </Text>
            </RateWrapper>
            <Layout.Group justify="flex-end" gap="spacing-3">
              <Button variant="secondary" onClick={onRejectClick} disabled={disabled}>
                Reject
              </Button>
              <Button variant="primary" onClick={onAcceptClick} disabled={disabled}>
                Accept
              </Button>
            </Layout.Group>
          </ActionsWrapper>
        </Layout.Group>
        <HideMobile>
          <Layout.Box paddingX="spacing-6" paddingY="spacing-3">
            <ExpirationContent>
              <Text variant="body-md">
                Offer expires in{' '}
                <Text variant="body-md-bold">
                  {hoursString}h{minutesString}m
                </Text>
              </Text>
              <ProgressBarCountdown max={DEFAULT_MAX_PROGRESS_BAR} value={progressValue} />
            </ExpirationContent>
          </Layout.Box>
        </HideMobile>
        <Layout.Stack gap="spacing-2" paddingX="spacing-6" paddingY="spacing-3">
          <Accordion style={{ backgroundColor: toCSSValue('color-background-secondary') }}>
            <Accordion.Title>
              <Layout.Group gap="spacing-2" align="center">
                <Icon name={getModeIconName(tender.mode)} size="24px" />
                <Text variant="body-md">
                  {tender.mode.toUpperCase()} &bull;{' '}
                  {tender.equipmentTypeLabel || formatEquipmentType(tender.equipmentType)}
                  {totalWeight ? ` • ${formatWeight(totalWeight)}` : ''}
                </Text>
              </Layout.Group>
            </Accordion.Title>
            <Accordion.Content>
              <Divider
                style={{
                  '--m-divider-margin-bottom': `${getToken('spacing-8')}`,
                }}
              />
              <MoreInfoSectionWrapper gap="spacing-1">
                <ColumnWrapper>
                  <Layout.Stack gap="spacing-4">
                    <StopsTimeline
                      stops={tender.stops}
                      titleFormatter={formatDescription}
                      superTitleFormatter={formatAddress}
                      subTitleFormatter={formatZip}
                      tags
                    />
                  </Layout.Stack>
                </ColumnWrapper>
                <ColumnWrapper gap="spacing-6">
                  {(tender.shipper.name || tender.shipper.email || tender.shipper.phoneNumber) && (
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Shipper contact
                      </Text>
                      <Layout.Group gap="spacing-1">
                        {tender.managedBy?.contactName && (
                          <StyledText color="color-text-tertiary">
                            {tender.managedBy?.contactName}
                          </StyledText>
                        )}
                        {tender.managedBy?.contactEmail && (
                          <StyledText color="color-text-tertiary">
                            {tender.managedBy?.contactEmail}
                          </StyledText>
                        )}
                        {tender.shipper.phoneNumber && (
                          <StyledText color="color-text-tertiary">
                            {tender.shipper.phoneNumber}
                          </StyledText>
                        )}
                      </Layout.Group>
                    </Layout.Stack>
                  )}

                  <Layout.Group align="center">
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Mode
                      </Text>
                      <Text variant="body-md" color="color-text-tertiary">
                        {tender.mode}
                      </Text>
                    </Layout.Stack>
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Equipment type
                      </Text>
                      <Text variant="body-md" color="color-text-tertiary">
                        {tender.equipmentTypeLabel || formatEquipmentType(tender.equipmentType)}
                        {tender.equipmentType === 'RFR' &&
                          ` • ${formatTemperature(
                            tender.equipmentRequirements?.temperature,
                            tender.equipmentRequirements?.temperatureUnit
                          )}`}
                      </Text>
                    </Layout.Stack>
                    {tender.shipperGuideId && (
                      <Layout.Stack gap="spacing-0-5">
                        <Text variant="body-md-bold" color="color-text-primary">
                          ShipperGuide ID
                        </Text>
                        <Text variant="body-md" color="color-text-tertiary">
                          {tender.shipperGuideId}
                        </Text>
                      </Layout.Stack>
                    )}
                  </Layout.Group>

                  <Layout.Stack gap="spacing-0-5">
                    <Text variant="body-md-bold" color="color-text-primary">
                      Accessorial charges
                    </Text>
                    {tender.accessorials.length > 0 ? (
                      <Layout.Group gap="spacing-1">
                        {tender.accessorials.map(accessorial => (
                          <Tag key={accessorial.code} size="small" variant="warning">
                            {accessorial.name}
                          </Tag>
                        ))}
                      </Layout.Group>
                    ) : (
                      <Text variant="body-sm" color="color-text-secondary">
                        No accessorial charges
                      </Text>
                    )}
                  </Layout.Stack>

                  {tender.notes && (
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Shipper notes
                      </Text>
                      <NotesList>
                        <li>
                          <Text variant="body-md" color="color-text-secondary">
                            {tender.notes}
                          </Text>
                        </li>
                      </NotesList>
                    </Layout.Stack>
                  )}

                  <AttachmentList
                    tenderAttachments={tender.attachments}
                    quoteUuid={tender.quoteUUID}
                  />
                </ColumnWrapper>
                <ColumnWrapper>
                  {tender.items && tender.items.length > 0 ? (
                    <ShippingItems
                      measures={tender.measures}
                      shippingItems={tender.items}
                      showOrderNumbers={false}
                    />
                  ) : (
                    <Layout.Box
                      borderColor="color-divider"
                      borderWidth="border-thin"
                      backgroundColor="color-neutral-20"
                      padding="spacing-6"
                    >
                      <Layout.Stack gap="spacing-2">
                        <Text variant="body-md-bold" color="color-text-primary">
                          Shipping items
                        </Text>
                        <Text>No items added</Text>
                      </Layout.Stack>
                    </Layout.Box>
                  )}
                </ColumnWrapper>
              </MoreInfoSectionWrapper>
            </Accordion.Content>
          </Accordion>
        </Layout.Stack>
      </Layout.Stack>
    </StyledLayout>
  )
}
